import React from 'react'
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material'
import { useListContext } from 'react-admin'
import { Property } from '../types'
import { PropertyCardUnified } from './PropertyCardUnified'
import { usePriceBadgeBatch } from '../hooks/usePriceBadge'
import type { PromoBadge } from '@repo/supabase'

interface PropertyGridListProps {
  promoBadges?: PromoBadge[]
  promoModuleEnabled?: boolean
  onManageKafelki?: () => void
}

export const PropertyGridList: React.FC<PropertyGridListProps> = ({ promoBadges = [], promoModuleEnabled = false, onManageKafelki }) => {
  const { data } = useListContext<Property>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'))

  const { badgeMap } = usePriceBadgeBatch(data ?? [])

  if (!data) {
    return null
  }

  // Smart sorting - extract numbers from title and sort numerically
  const sortedData = [...data].sort((a, b) => {
    const aNumbers = a.title.match(/\d+/g)
    const bNumbers = b.title.match(/\d+/g)

    if (aNumbers && bNumbers) {
      // Compare by first number found
      const aNum = parseInt(aNumbers[0])
      const bNum = parseInt(bNumbers[0])
      return aNum - bNum // ASC order (smallest first)
    }

    // Fallback to alphabetical ASC if no numbers
    return a.title.localeCompare(b.title)
  })

  // Responsive grid columns
  const getGridColumns = () => {
    if (isMobile) return 1 // 1 column on mobile
    if (isTablet) return 2 // 2 columns on tablet
    return 3 // 3 columns on desktop
  }

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {sortedData.map((record) => (
          <Grid
            item
            xs={12}  // Mobile: full width
            sm={12}  // Small tablet: full width
            md={6}   // Medium: 2 columns
            lg={4}   // Large: 3 columns
            xl={3}   // Extra large: 4 columns
            key={`${record.id}-${record.total_price}-${record.status_code}`}
            sx={{
              minWidth: isMobile ? '100%' : '400px' // Increased minimum width
            }}
          >
            <PropertyCardUnified
              record={record}
              priceBadgeData={badgeMap[record.id]}
              promoBadges={promoBadges}
              promoModuleEnabled={promoModuleEnabled}
              onManageKafelki={onManageKafelki}
              key={`card-${record.id}-${record.total_price}-${record.status_code}`}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}